import NavigatedViewer from 'bpmn-js/lib/NavigatedViewer'
import { memo, useEffect, useRef, useState } from 'react'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import 'bpmn-js/dist/assets/diagram-js.css'
import 'bpmn-js/dist/assets/bpmn-js.css'
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css'

interface Canvas {
	zoom(value: 'fit-viewport'): number
}

interface BpmnPreviewProps {
	xml: string | null
	className?: string
}

const BpmnPreviewInner = ({ xml, className = '' }: BpmnPreviewProps) => {
	const { t } = useTranslation()
	const containerRef = useRef<HTMLDivElement>(null)
	const viewerRef = useRef<NavigatedViewer | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		let cancelled = false

		const init = async () => {
			setLoading(true)
			setError(null)

			// Yield until after paint so the viewer container has dimensions before importXML/zoom.
			await new Promise((resolve) => requestAnimationFrame(resolve))
			await new Promise((resolve) => setTimeout(resolve, 50))

			if (cancelled || !containerRef.current || !xml) {
				setLoading(false)
				return
			}

			try {
				const viewer = new NavigatedViewer({ container: containerRef.current })
				viewerRef.current = viewer

				await viewer.importXML(xml)

				if (!cancelled) {
					const canvas = viewer.get('canvas') as Canvas
					canvas.zoom('fit-viewport')
					setLoading(false)
				}
			} catch (err) {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : t('components.bpmnPreview.errorLoading'))
					setLoading(false)
				}
			}
		}

		init()

		return () => {
			cancelled = true
			if (viewerRef.current) {
				try {
					viewerRef.current.destroy()
				} catch {
					// ignore destroy errors
				}
				viewerRef.current = null
			}
		}
	}, [xml, t])

	if (!xml) return null

	return (
		<div
			className={cn('relative overflow-hidden rounded-lg border border-border bg-gray-50', className)}
		>
			<div ref={containerRef} className="h-full w-full" />
			{loading && (
				<div className="absolute inset-0 flex items-center justify-center bg-gray-50">
					<Spinner className="h-6 w-6" />
				</div>
			)}
			{error && !loading && (
				<div className="absolute inset-0 flex items-center justify-center bg-red-50 p-4">
					<p className="text-center text-sm text-red-600">{error}</p>
				</div>
			)}
		</div>
	)
}

export const BpmnPreview = memo(BpmnPreviewInner)
